import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import {
    ApiResponse, ListaAudios, AudioCliente, Cliente,
    ESTADO_AUDIO_LABELS, ESTADO_AUDIO_COLORS,
    URGENCIA_AUDIO_LABELS, URGENCIA_AUDIO_COLORS,
    ESTADOS_AUDIO_INTERMEDIOS
} from '../types';
import { AudioDetalleModal } from './AudioDetalleModal';
import { SubirAudioModal } from './SubirAudioModal';
import { ColaAudiosPanel } from './ColaAudiosPanel';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const POLLING_MS = 10000;

const formatFecha = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatDuracion = (segundos: number | null): string => {
    if (segundos === null) return '—';
    const m = Math.floor(segundos / 60);
    const s = segundos % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const POR_PAGINA = 20;

export const VistaAudios: React.FC = () => {
    const [data, setData] = useState<ListaAudios | null>(null);
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [loading, setLoading] = useState(false);
    const [filtroEstado, setFiltroEstado] = useState<string>('');
    const [filtroCliente, setFiltroCliente] = useState<string>('');
    const [pagina, setPagina] = useState(1);
    const [audioAbierto, setAudioAbierto] = useState<AudioCliente | null>(null);
    const [mostrarSubir, setMostrarSubir] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // El backend pagina de entrada (por_pagina default 20, GET /api/audios) —
    // antes esta vista nunca mandaba `pagina` ni mostraba que hubiera más de
    // una página, así que con más de 20 audios el resto quedaba inaccesible
    // sin ningún aviso (ni siquiera el total real se mostraba en ningún lado).
    const fetchAudios = useCallback(async () => {
        const params = new URLSearchParams();
        if (filtroEstado) params.set('estado', filtroEstado);
        if (filtroCliente) params.set('cliente_id', filtroCliente);
        params.set('pagina', String(pagina));
        params.set('por_pagina', String(POR_PAGINA));
        try {
            const res = await apiFetch(`${API_BASE}/audios?${params.toString()}`);
            const json: ApiResponse<ListaAudios> = await res.json();
            if (json.success && json.data) setData(json.data);
        } catch (e) {
            console.error(e);
        }
    }, [filtroEstado, filtroCliente, pagina]);

    // Cambiar de filtro con una página > 1 activa podía dejar la vista
    // "vacía" (ej. página 3 de un filtro que ahora solo tiene 1 página) sin
    // que se note que es por el filtro y no porque no haya resultados.
    useEffect(() => { setPagina(1); }, [filtroEstado, filtroCliente]);

    const fetchClientes = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_BASE}/clientes`);
            const json: ApiResponse<Cliente[]> = await res.json();
            if (json.success && json.data) setClientes(json.data);
        } catch (e) {
            console.error(e);
        }
    }, []);

    const cargarInicial = useCallback(async () => {
        setLoading(true);
        await Promise.all([fetchAudios(), fetchClientes()]);
        setLoading(false);
    }, [fetchAudios, fetchClientes]);

    useEffect(() => { cargarInicial(); }, [cargarInicial]);

    // Polling cada 10s solo mientras haya audios en un estado intermedio.
    useEffect(() => {
        const hayIntermedios = data?.items.some(a => ESTADOS_AUDIO_INTERMEDIOS.includes(a.estado)) ?? false;

        if (hayIntermedios && !intervalRef.current) {
            intervalRef.current = setInterval(fetchAudios, POLLING_MS);
        } else if (!hayIntermedios && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [data, fetchAudios]);

    return (
        <div>
            <ColaAudiosPanel />

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                        🎙️ Audios de Clientes
                        {data && data.total > 0 && (
                            <span className="ml-2 text-sm font-normal text-slate-500">({data.total})</span>
                        )}
                    </h2>
                    <p className="text-sm text-slate-500">Transcripción y análisis con IA — nada se envía sin tu aprobación</p>
                </div>
                <button
                    onClick={() => setMostrarSubir(true)}
                    className="px-3 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700"
                >
                    + Subir audio
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                <select
                    value={filtroCliente}
                    onChange={e => setFiltroCliente(e.target.value)}
                    className="text-sm border border-slate-300 rounded-lg px-2 py-1.5"
                >
                    <option value="">Todos los clientes</option>
                    {clientes.map(c => (
                        <option key={c.id} value={c.id}>{c.empresa}</option>
                    ))}
                </select>
                <select
                    value={filtroEstado}
                    onChange={e => setFiltroEstado(e.target.value)}
                    className="text-sm border border-slate-300 rounded-lg px-2 py-1.5"
                >
                    <option value="">Todos los estados</option>
                    {Object.entries(ESTADO_AUDIO_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>
                <button
                    onClick={fetchAudios}
                    className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg"
                >
                    ↻ Refrescar
                </button>
            </div>

            {loading && !data ? (
                <div className="text-sm text-slate-500 italic py-8 text-center">Cargando...</div>
            ) : !data || data.items.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-8 text-center border border-dashed border-slate-200 rounded-lg">
                    No hay audios todavía
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left px-4 py-2 font-medium text-slate-500">Cliente</th>
                                <th className="text-left px-4 py-2 font-medium text-slate-500">Fecha</th>
                                <th className="text-left px-4 py-2 font-medium text-slate-500">Duración</th>
                                <th className="text-left px-4 py-2 font-medium text-slate-500">Estado</th>
                                <th className="text-left px-4 py-2 font-medium text-slate-500">Urgencia</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {data.items.map(audio => (
                                <tr
                                    key={audio.id}
                                    onClick={() => setAudioAbierto(audio)}
                                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                                >
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-1 h-4 rounded-full" style={{ backgroundColor: audio.cliente_color }} />
                                            <span className="font-medium text-slate-800">{audio.empresa}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-500">{formatFecha(audio.creado_en)}</td>
                                    <td className="px-4 py-2.5 text-slate-500 font-mono">{formatDuracion(audio.duracion_segundos)}</td>
                                    <td className="px-4 py-2.5">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${ESTADO_AUDIO_COLORS[audio.estado]}`}>
                                            {ESTADO_AUDIO_LABELS[audio.estado]}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        {audio.analisis && (
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${URGENCIA_AUDIO_COLORS[audio.analisis.urgencia]}`}>
                                                {URGENCIA_AUDIO_LABELS[audio.analisis.urgencia]}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {data.total > POR_PAGINA && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
                        <span className="text-slate-500">
                            {(pagina - 1) * POR_PAGINA + 1}–{Math.min(pagina * POR_PAGINA, data.total)} de {data.total}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPagina(p => Math.max(1, p - 1))}
                                disabled={pagina === 1}
                                className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                ← Anterior
                            </button>
                            <span className="text-xs text-slate-500">
                                Página {pagina} de {Math.ceil(data.total / POR_PAGINA)}
                            </span>
                            <button
                                onClick={() => setPagina(p => p + 1)}
                                disabled={pagina * POR_PAGINA >= data.total}
                                className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Siguiente →
                            </button>
                        </div>
                    </div>
                )}
                </div>
            )}

            {audioAbierto && (
                <AudioDetalleModal
                    audio={audioAbierto}
                    onClose={() => setAudioAbierto(null)}
                    onActualizado={fetchAudios}
                />
            )}

            {mostrarSubir && (
                <SubirAudioModal
                    clientes={clientes}
                    onClose={() => setMostrarSubir(false)}
                    onSubido={() => { setMostrarSubir(false); fetchAudios(); }}
                />
            )}
        </div>
    );
};
